/**
 * lib/tradingMode.ts -- PAPER or LIVE, from the router's one source.
 *
 * PURE: no imports. Executed by _test/live-confirm.mjs.
 *
 * WHERE THE ANSWER COMES FROM.
 *
 * The router decides whether an order moves real money with
 * tradingModeFor(broker, creds) (core/brokers/dispatch.ts) -- over the SAME
 * credentials the order will carry -- and the order gate refuses a real-money
 * order without `confirmed_mode: "live"` (409 TRADING_MODE_MISMATCH). It
 * reports that same answer per broker on GET /api/broker/status:
 *
 *   { brokers: [{ name, connected, mode, real_money }], mode_source: "credentials" }
 *
 * where mode is "paper" | "demo" | "live" | "unknown" | "disconnected", and
 * "unknown" (IBKR: the token decides and cannot be proven paper) is real money.
 *
 * The ticket's badge used to be derived in the browser from the vault ENTRY
 * NAME ("alpaca_paper" -> paper). That is a second classifier, and the one
 * that drifts is the one that decides whether a dialog says PAPER while the
 * order goes LIVE. The badge now reads the router's row and nothing else; when
 * the row cannot be read the mode is UNKNOWN and orders are refused, never
 * defaulted.
 */

export type RouterMode = 'paper' | 'demo' | 'live' | 'unknown' | 'disconnected';

export interface BrokerStatusRow {
  name:        string;
  connected:   boolean;
  mode:        string;
  real_money?: boolean;
}
export interface BrokerStatus {
  broker?:          string | null;
  mode?:            string;
  connected?:       boolean;
  brokers?:         BrokerStatusRow[];
  connected_count?: number;
  mode_source?:     string;
}

export interface ModeInfo {
  /** False when the router's answer is not available: do not trade on it. */
  known:     boolean;
  connected: boolean;
  mode:      RouterMode | null;
  realMoney: boolean;
  /** What the badge says. */
  label:     string;
}

const UNKNOWN: ModeInfo = { known: false, connected: false, mode: null, realMoney: true, label: 'MODE UNKNOWN' };

/** The router's answer for one broker, or UNKNOWN. Never a default of "paper". */
export function modeFromStatus(status: BrokerStatus | null | undefined, broker: string): ModeInfo {
  // Only trust a response that says it was computed from the caller's
  // credentials. An older router reported the PLATFORM env here, which is
  // exactly the disagreement this module exists to remove.
  if (!status || status.mode_source !== 'credentials' || !Array.isArray(status.brokers)) return UNKNOWN;
  const row = status.brokers.find((b) => b && b.name === broker);
  if (!row) return UNKNOWN;
  const m = String(row.mode ?? '').toLowerCase();
  if (!row.connected || m === 'disconnected') {
    return { known: true, connected: false, mode: 'disconnected', realMoney: false, label: 'DISCONNECTED' };
  }
  if (m !== 'paper' && m !== 'demo' && m !== 'live' && m !== 'unknown') return UNKNOWN;
  // real_money is the router's own flag; when absent, apply the router's rule
  // (tradesRealMoney: live or unknown).
  const realMoney = typeof row.real_money === 'boolean' ? row.real_money : (m === 'live' || m === 'unknown');
  const label = m === 'unknown'
    ? 'LIVE? (IBKR -- cannot be proven paper; treated as REAL MONEY)'
    : m.toUpperCase();
  return { known: true, connected: true, mode: m, realMoney, label };
}

/**
 * The `confirmed_mode` to send, or null when the order must NOT be sent.
 *
 * - Real money: "live", and ONLY when the trader explicitly confirmed live
 *   trading for this order. Silence is not consent; null here means "do not
 *   send", and the router would refuse it anyway.
 * - Paper/demo: echo the mode the dialog showed. The router refuses a
 *   mismatch, so a dialog that said PAPER can never be the click that sent a
 *   LIVE order.
 * - Unknown / disconnected: null. We do not trade on a mode we could not read.
 */
export function confirmedModeFor(info: ModeInfo, liveConfirmed: boolean): 'live' | 'paper' | 'demo' | null {
  if (!info.known || !info.connected || !info.mode) return null;
  if (info.realMoney) return liveConfirmed ? 'live' : null;
  if (info.mode === 'paper' || info.mode === 'demo') return info.mode;
  return null;
}

/** For a fan-out: any real-money leg makes the whole request need "live". */
export function confirmedModeForMany(infos: ModeInfo[], liveConfirmed: boolean): 'live' | 'paper' | 'demo' | null {
  if (infos.length === 0 || infos.some((i) => !i.known || !i.connected)) return null;
  if (infos.some((i) => i.realMoney)) return liveConfirmed ? 'live' : null;
  // All simulated. The multi route only checks for "live" when a leg is real
  // money, so the echo is informational; send the first leg's mode.
  return confirmedModeFor(infos[0], false);
}

/** The phrase the trader must type to confirm real-money trading. */
export const LIVE_CONFIRM_PHRASE = 'LIVE';

/** True only for an explicit, exact typed confirmation. */
export function isExplicitLiveConfirmation(typed: string | null | undefined): boolean {
  return typeof typed === 'string' && typed.trim().toUpperCase() === LIVE_CONFIRM_PHRASE;
}

/**
 * POST /api/broker/passthrough: the HOST decides (the base is pinned per
 * passthrough broker key), and these keys are real-money hosts -- mirrors the
 * router's PASSTHROUGH_REAL_MONEY (brokerVaultRoutes.ts). IBKR is in the set
 * because its token cannot be proven paper.
 */
export const PASSTHROUGH_REAL_MONEY: ReadonlySet<string> = new Set(['alpaca', 'ibkr', 'tradovate_live']);

/**
 * The confirmed_mode a passthrough call must carry, or 'REFUSE' when it must
 * not be sent. The router treats every non-GET as order-bearing (SSRF-01), so
 * a non-GET to a real-money host needs an explicit live confirmation; a GET
 * needs none.
 */
export function passthroughConfirmedMode(brokerKey: string, method: string, liveConfirmed: boolean): 'live' | null | 'REFUSE' {
  const m = method.toUpperCase();
  if (m === 'GET' || m === 'HEAD') return null;
  if (!PASSTHROUGH_REAL_MONEY.has(brokerKey)) return null;
  return liveConfirmed ? 'live' : 'REFUSE';
}
